import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Get API URL from environment variable or use default
const API_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// fetchBrands thunk — accepts optional { limit, offset, append, search } for paginated loads.
//   - With no args: fetches ALL brands (legacy behaviour)
//   - With { limit, offset }: fetches one page; reducer replaces list
//   - With { limit, offset, append: true }: fetches page; reducer appends to existing list
//   - With { search }: the server narrows by brand name or domain. It has to be
//     the server's job — only a page of brands is loaded at a time, so filtering
//     here would only ever search the five cards already on screen.
export const fetchBrands = createAsyncThunk(
  'brands/fetchBrands',
  async (args = {}, { rejectWithValue }) => {
    const { limit, offset, append = false, search = '' } = args || {};
    const paginated = Number.isFinite(limit) && Number.isFinite(offset);
    const term = typeof search === 'string' ? search.trim() : '';

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 480000);

      const query = paginated ? `limit=${limit}&offset=${offset}` : '';
      const searchQuery = term ? `search=${encodeURIComponent(term)}` : '';
      const qs = [query, searchQuery].filter(Boolean).join('&');
      const url = `${API_URL}/api/brands/dashboard${qs ? `?${qs}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        mode: 'cors',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      let data;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        throw new Error(data?.message || `HTTP error! status: ${response.status}`);
      }

      if (data && data.success) {
        // Return shape varies: with pagination we also have total + offset + append flag
        return paginated
          ? {
              brands: data.data || [],
              total: data.total ?? null,
              // How many brands the search matched. An older server doesn't
              // send it; then the page total is all we have.
              matched: data.matched ?? data.total ?? null,
              offset: data.offset ?? offset,
              append,
              search: term
            }
          : data.data || [];
      } else {
        throw new Error(data?.message || 'API returned unsuccessful response');
      }
      
    } catch (err) {
      let errorMessage = 'An error occurred while fetching brands';
      
      if (err.name === 'AbortError') {
        errorMessage = 'Dashboard request timed out after 8 minutes. The system may be processing large amounts of data. Please try again.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// V2 createBrand — POST /api/addbrandv2/add responds in seconds with the created
// brand row; the heavy pipeline runs server-side in the background and is
// tracked via fetchJobs polling. No fake popup, no per-brand intervals.
//
// `brandData.verticalMode === 'trading'` posts to /api/forexaddbrand/add
// instead. That endpoint runs the SAME pipeline with the Trading & Investment
// classifier substituted in, and writes its job with kind='forex_add' — which
// /api/addbrandv2/status already polls, so job progress, the brand card and the
// live-check bulb all work with no further change here.
export const createBrand = createAsyncThunk(
  'brands/createBrand',
  async (brandData, { rejectWithValue }) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 60000);

      const { verticalMode, ...payload } = brandData || {};
      const endpoint = verticalMode === 'trading'
        ? `${API_URL}/api/forexaddbrand/add`
        : `${API_URL}/api/addbrandv2/add`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        mode: 'cors',
        signal: controller.signal,
        body: JSON.stringify(payload)
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        brand: data.brand,
        job: data.job || null,
        skipProcessing: !!data.skipProcessing
      };
    } catch (error) {
      let errorMessage = 'Network error. Please check your connection and try again.';

      if (error.name === 'AbortError') {
        errorMessage = 'Adding the brand timed out. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// V2 job polling — one request returns every running/recent brand job plus
// live-check ("bulb") states for the logged-in user. Dispatched on a single
// interval from BrandsSection while any work is in flight.
export const fetchJobs = createAsyncThunk(
  'brands/fetchJobs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/api/addbrandv2/status`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
        mode: 'cors'
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch job status');
      }
      return { jobs: data.jobs || [], liveChecks: data.liveChecks || [] };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch job status');
    }
  }
);

// Polling thunk for processing status - NO userId PARAMETER NEEDED
export const pollProcessingStatus = createAsyncThunk(
  'brands/pollProcessingStatus',
  async ({ brandUrl }, { rejectWithValue }) => {
    try {
      // userId will be fetched from session on backend
      const response = await fetch(`${API_URL}/api/addbrand/polling/${brandUrl}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include', // This sends the session cookie
        mode: 'cors'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        brandUrl,
        processing_status: data.processing_status
      };

    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch processing status');
    }
  }
);

const initialState = {
  brands: [],
  loading: false,        // initial load / replace
  loadingMore: false,    // appending the next page
  total: null,           // every brand on the account; null = unknown
  matched: null,         // how many the current search found (= total when not searching)
  search: '',            // the term the loaded list belongs to
  error: null,
  createLoading: false,
  createError: null,
  createSuccess: false,
  loadingMessage: '',
  lastFetchTime: null,
  // V2 background jobs, keyed by brand_id: { status, stage, stage_label, percent, detail, error }
  jobs: {},
  // V2 live-affiliate check ("bulb"), keyed by brand_id: 'pending' | 'running'
  liveChecks: {},
  // Processing status tracking
  processingBrands: {}, // { brandUrl: { status: '1', step: 'Fetching backlinks...', brandName: 'Example' } }
  isPolling: false,
  // Show processing popup flag
  showProcessingPopup: false,
  currentProcessingBrand: null,
  // Active polling intervals
  pollingIntervals: {} // Store interval IDs for cleanup
};

const brandsSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {
    clearCreateStatus: (state) => {
      state.createError = null;
      state.createSuccess = false;
      state.showProcessingPopup = false;
      state.currentProcessingBrand = null;
    },
    setLoadingMessage: (state, action) => {
      state.loadingMessage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Start polling for a brand
    startPolling: (state, action) => {
      const { brandUrl, brandName } = action.payload;
      
      state.processingBrands[brandUrl] = {
        status: '1',
        step: 'Initializing brand analysis...',
        isActive: true,
        brandName: brandName || brandUrl,
        startTime: new Date().toISOString()
      };
      state.isPolling = true;
      state.showProcessingPopup = true;
      state.currentProcessingBrand = brandUrl;
    },
    // Stop polling for a brand
    stopPolling: (state, action) => {
      const { brandUrl } = action.payload;
      
      if (state.processingBrands[brandUrl]) {
        state.processingBrands[brandUrl].isActive = false;
        state.processingBrands[brandUrl].endTime = new Date().toISOString();
      }
      
      // Check if any brands are still being polled
      const hasActivePolling = Object.values(state.processingBrands).some(brand => brand.isActive);
      state.isPolling = hasActivePolling;
      
      // Hide popup if no active polling
      if (!hasActivePolling) {
        state.showProcessingPopup = false;
        state.currentProcessingBrand = null;
      }
    },
    // Update processing step message
    updateProcessingStep: (state, action) => {
      const { brandUrl, step } = action.payload;
      if (state.processingBrands[brandUrl]) {
        state.processingBrands[brandUrl].step = step;
      }
    },
    // Hide processing popup manually
    hideProcessingPopup: (state) => {
      state.showProcessingPopup = false;
      state.currentProcessingBrand = null;
    },
    // Show processing popup manually
    showProcessingPopupAction: (state, action) => {
      const { brandUrl } = action.payload;
      state.showProcessingPopup = true;
      state.currentProcessingBrand = brandUrl;
    },
    // Clean up completed processing brands
    cleanupProcessingBrands: (state) => {
      const cutoffTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // 24 hours ago
      
      Object.keys(state.processingBrands).forEach(brandUrl => {
        const brand = state.processingBrands[brandUrl];
        if (!brand.isActive && brand.endTime && brand.endTime < cutoffTime) {
          delete state.processingBrands[brandUrl];
        }
      });
    },
    // Set polling interval ID for cleanup
    setPollingInterval: (state, action) => {
      const { brandUrl, intervalId } = action.payload;
      state.pollingIntervals[brandUrl] = intervalId;
    },
    // Clear polling interval
    clearPollingInterval: (state, action) => {
      const { brandUrl } = action.payload;
      if (state.pollingIntervals[brandUrl]) {
        delete state.pollingIntervals[brandUrl];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchBrands — supports both full-list and paginated (append/replace) modes.
      .addCase(fetchBrands.pending, (state, action) => {
        const append = action.meta?.arg?.append === true;
        if (append) {
          state.loadingMore = true;
        } else {
          state.loading = true;
          state.loadingMessage = 'Loading dashboard data...';
        }
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = null;
        state.loadingMessage = '';
        state.lastFetchTime = new Date().toISOString();

        const payload = action.payload;
        if (Array.isArray(payload)) {
          // Legacy non-paginated response — replace list, total unknown
          state.brands = payload;
          state.total = payload.length;
          state.matched = payload.length;
        } else if (payload && Array.isArray(payload.brands)) {
          // Paginated response
          state.total = payload.total ?? state.total;
          state.matched = payload.matched ?? payload.total ?? state.matched;
          state.search = payload.search || '';
          if (payload.append) {
            // Dedupe by id when appending so a refresh of an overlapping page
            // doesn't show two copies of the same card.
            const existingIds = new Set(state.brands.map(b => b.id));
            for (const b of payload.brands) {
              if (!existingIds.has(b.id)) state.brands.push(b);
            }
          } else {
            state.brands = payload.brands;
          }
        }
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
        state.loadingMessage = '';
        // Don't clear brands on a failed "load more" — user keeps what they have
        if (action.meta?.arg?.append !== true) {
          state.brands = [];
        }
      })
      // Updated create brand cases
      .addCase(createBrand.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.createSuccess = false;
        state.showProcessingPopup = false;
      })
      .addCase(createBrand.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.createError = null;

        const { brand, job } = action.payload || {};
        if (brand) {
          // Card appears immediately — newest first, no duplicates
          const existingIndex = state.brands.findIndex(
            b => b.id === brand.id ||
                 (b.brand_url === brand.brand_url && b.user_id === brand.user_id)
          );
          if (existingIndex === -1) {
            state.brands.unshift(brand);
            if (typeof state.total === 'number') state.total += 1;
            // The card is on screen whatever the search box says, so the count
            // the list pages through has to grow with it.
            if (typeof state.matched === 'number') state.matched += 1;
          } else {
            state.brands[existingIndex] = { ...state.brands[existingIndex], ...brand };
          }

          if (job) {
            state.jobs[brand.id] = {
              brand_id: brand.id,
              brand_url: brand.brand_url,
              status: job.status || 'running',
              stage: job.stage || 'collect',
              stage_label: job.stage_label || 'Fetching referral websites',
              percent: job.percent || 0,
              detail: job.detail || {}
            };
          }
        }
      })
      .addCase(createBrand.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
        state.createSuccess = false;
        state.showProcessingPopup = false;
      })
      // Polling processing status cases - UPDATED FOR 8 STEPS
      .addCase(pollProcessingStatus.fulfilled, (state, action) => {
        const { brandUrl, processing_status } = action.payload;
        
        if (state.processingBrands[brandUrl]) {
          state.processingBrands[brandUrl].status = processing_status;
          
          // Update step message based on status - NOW 8 STEPS + COMPLETED
          const stepMessages = {
            '1': 'Initializing brand analysis...',
            '2': 'Fetching backlinks...',
            '3': 'Performing Google search analysis...',
            '4': 'Classifying affiliate URLs...',
            '5': 'Processing backlink data...',
            '6': 'Analyzing organic keywords...',
            '7': 'Processing paid keywords...',
            '8': 'Finalizing geographic data...',
            'completed': 'Analysis completed successfully!',
            'failed': 'Processing failed. Please try again.'
          };
          
          state.processingBrands[brandUrl].step = stepMessages[processing_status] || `Processing step ${processing_status}...`;
          
          // Stop polling if completed or failed
          if (processing_status === 'completed' || processing_status === 'failed' || parseInt(processing_status) >= 8) {
            state.processingBrands[brandUrl].isActive = false;
            state.processingBrands[brandUrl].endTime = new Date().toISOString();
            
            // Check if any brands are still being polled
            const hasActivePolling = Object.values(state.processingBrands).some(brand => brand.isActive);
            state.isPolling = hasActivePolling;
            
            // Hide popup if no active polling
            if (!hasActivePolling) {
              state.showProcessingPopup = false;
              state.currentProcessingBrand = null;
            }
          }
        }
      })
      .addCase(pollProcessingStatus.rejected, (state, action) => {
        // Log polling errors but don't stop polling completely
        console.error('Polling error:', action.payload);

        // If error persists for a brand, you might want to mark it as failed
        // This is optional - you can handle this based on your requirements
      })
      // V2 job polling — one response covers every in-flight brand
      .addCase(fetchJobs.fulfilled, (state, action) => {
        const { jobs, liveChecks } = action.payload;

        const nextJobs = {};
        for (const job of jobs) {
          nextJobs[job.brand_id] = job;

          // Keep the brand row's status in sync so cards flip without a refetch
          const brand = state.brands.find(b => b.id === job.brand_id);
          if (brand) {
            if (job.status === 'completed') brand.processing_status = 'completed';
            else if (job.status === 'failed') brand.processing_status = 'failed';
          }
        }
        state.jobs = nextJobs;

        const nextLive = {};
        for (const lc of liveChecks) {
          nextLive[lc.brand_id] = lc.live_check_status;

          // The status poll carries the brand's current live-affiliate count,
          // so the card's number ticks down as not-live affiliates are removed
          // mid-check instead of jumping only when the check completes.
          if (lc.live_affiliates_count !== undefined && lc.live_affiliates_count !== null) {
            const brand = state.brands.find(b => b.id === lc.brand_id);
            if (brand) {
              brand.live_affiliates_count = parseInt(lc.live_affiliates_count, 10) || 0;
            }
          }
        }
        state.liveChecks = nextLive;

        // A brand that WAS live-checking but is no longer in the response has
        // finished — clear the stale row value too, otherwise BrandCard's
        // fallback (brand.live_check_status) keeps the bulb on forever.
        for (const brand of state.brands) {
          const wasChecking = brand.live_check_status === 'pending' || brand.live_check_status === 'running';
          if (wasChecking && !nextLive[brand.id]) {
            brand.live_check_status = 'completed';
          }
        }
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        // Non-fatal: keep last known job state, next poll retries
        console.error('Job polling error:', action.payload);
      });
  }
});

// Action creators
export const { 
  clearCreateStatus, 
  setLoadingMessage, 
  clearError,
  startPolling,
  stopPolling,
  updateProcessingStep,
  hideProcessingPopup,
  showProcessingPopupAction,
  cleanupProcessingBrands,
  setPollingInterval,
  clearPollingInterval
} = brandsSlice.actions;

// Thunk for managing polling lifecycle
export const startPollingWithCleanup = (brandData) => (dispatch, getState) => {
  const { brandUrl, brandName } = brandData;
  
  // Start polling in state
  dispatch(startPolling({ brandUrl, brandName }));
  
  // Set up polling interval
  const intervalId = setInterval(() => {
    const state = getState();
    const processingInfo = state.brands.processingBrands[brandUrl];
    
    // Only poll if still active
    if (processingInfo?.isActive) {
      dispatch(pollProcessingStatus({ brandUrl }));
    } else {
      // Clean up interval if no longer active
      clearInterval(intervalId);
      dispatch(clearPollingInterval({ brandUrl }));
    }
  }, 3000); // Poll every 3 seconds
  
  // Store interval ID for cleanup
  dispatch(setPollingInterval({ brandUrl, intervalId }));
  
  return intervalId;
};

// Thunk for stopping polling with cleanup
export const stopPollingWithCleanup = (brandUrl) => (dispatch, getState) => {
  const state = getState();
  const intervalId = state.brands.pollingIntervals[brandUrl];
  
  // Clear the interval
  if (intervalId) {
    clearInterval(intervalId);
    dispatch(clearPollingInterval({ brandUrl }));
  }
  
  // Stop polling in state
  dispatch(stopPolling({ brandUrl }));
};

// Selector functions for easier state access
export const selectBrands = (state) => state.brands.brands;
export const selectBrandSearch = (state) => state.brands.search;
export const selectBrandsLoading = (state) => state.brands.loading;
export const selectBrandsError = (state) => state.brands.error;
export const selectCreateLoading = (state) => state.brands.createLoading;
export const selectCreateError = (state) => state.brands.createError;
export const selectCreateSuccess = (state) => state.brands.createSuccess;
export const selectProcessingBrands = (state) => state.brands.processingBrands;
export const selectIsPolling = (state) => state.brands.isPolling;
export const selectShowProcessingPopup = (state) => state.brands.showProcessingPopup;
export const selectCurrentProcessingBrand = (state) => state.brands.currentProcessingBrand;

// Selector for getting processing status of a specific brand
export const selectBrandProcessingStatus = (brandUrl) => (state) => {
  return state.brands.processingBrands[brandUrl] || null;
};

// V2 selectors
export const selectBrandJobs = (state) => state.brands.jobs;
export const selectBrandJob = (brandId) => (state) => state.brands.jobs[brandId] || null;
export const selectLiveChecks = (state) => state.brands.liveChecks;
export const selectHasActiveWork = (state) => {
  const jobsRunning = Object.values(state.brands.jobs).some(j => j.status === 'running');
  // Live checks count as active work whether we know from the poll (liveChecks)
  // or only from the brand row (freshly refetched brands) — otherwise polling
  // can stop while a bulb is still showing and never clear it.
  const liveRunning = Object.keys(state.brands.liveChecks).length > 0 ||
    state.brands.brands.some(b => b.live_check_status === 'running' || b.live_check_status === 'pending');
  const brandsProcessing = state.brands.brands.some(b => {
    const s = b.processing_status;
    const n = parseInt(s);
    return s !== 'completed' && s !== 'failed' && Number.isFinite(n) && n >= 1 && n < 8;
  });
  return jobsRunning || liveRunning || brandsProcessing;
};

// Selector for getting all active processing brands
export const selectActiveProcessingBrands = (state) => {
  return Object.entries(state.brands.processingBrands)
    .filter(([_, brand]) => brand.isActive)
    .reduce((acc, [brandUrl, brand]) => {
      acc[brandUrl] = brand;
      return acc;
    }, {});
};

export default brandsSlice.reducer;